import { useRef } from 'react';
import {
  AdyenCheckout,
  ApplePay,
  Card,
  Dropin,
  GooglePay,
  type CoreConfiguration,
} from '@adyen/adyen-web';
import useAsyncEffect from 'root/client/hooks/use-async-effect';
import { appendStylesheet } from 'root/client/ts/append-stylesheet';
import type { UseAdyenDropin as UseAdyenDropinProps } from './adyen-dropin.types';
import { isDefined } from 'list-fns';

const useAdyenDropin = ({
  dropinRef,
  config,
  supportGooglePay,
}: UseAdyenDropinProps) => {
  const hasMountedRef = useRef(false);
  const dropinInstanceRef = useRef<Dropin | null>(null);

  useAsyncEffect(async () => {
    if (!config.session) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    appendStylesheet(
      'https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/6.11.0/adyen.css',
      { crossorigin: 'anonymous' },
    );

    const initDropin = async () => {
      const handlePaymentFailed: CoreConfiguration['onPaymentFailed'] = (
        result,
        component,
      ) => {
        config.onPaymentFailed?.(result);
        if (component) {
          component.setStatus('ready');
        }
      };

      try {
        const checkout = await AdyenCheckout({
          clientKey: config.clientKey,
          environment: config.environment,
          locale: config.locale,
          session: config.session,
          onPaymentCompleted: config.onPaymentCompleted,
          onPaymentFailed: handlePaymentFailed,
        });

        const dropin = new Dropin(checkout, {
          openPaymentMethod: {
            type: 'scheme', // NOTE this is the type for the Card payment method
          },
          paymentMethodComponents: [
            Card,
            ApplePay,
            supportGooglePay ? GooglePay : undefined,
          ].filter(isDefined),
          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
              name: 'Cardholder',
              challengeWindowSize: '05',
            },
          },
        });
        dropin.mount(dropinRef.current!);
        dropinInstanceRef.current = dropin;
      } catch (err) {
        if (err instanceof Error && config.onError) {
          config.onError({
            name: err.name,
            message: err.message,
            cause: err.message,
            options: { cause: err.name },
          });
        }
      }
    };

    const delayAndMount = () => {
      if (hasMountedRef.current) {
        return;
      }

      if (!dropinRef.current && attempts < maxAttempts) {
        attempts++;
        setTimeout(delayAndMount, 100);
        return;
      }

      if (!dropinRef.current) {
        return;
      }

      hasMountedRef.current = true;
      initDropin();
    };

    delayAndMount();

    if (dropinInstanceRef.current) {
      dropinInstanceRef.current.unmount?.();
      dropinInstanceRef.current = null;
      hasMountedRef.current = false;
    }
  }, []);
};

export default useAdyenDropin;
